import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LayoutTemplate, Plus, Search, ChevronRight, Trash2, Star } from "lucide-react";
import type { LandingPageTemplate } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const DEFAULT_PAPA_BEAR_SECTIONS = [
  { sectionType: "announcement_bar", order: 0, enabled: true, variant: "default" },
  { sectionType: "hero_buy_box", order: 1, enabled: true, variant: "default" },
  { sectionType: "sticky_buy_bar", order: 2, enabled: true, variant: "default" },
  { sectionType: "product_recipe", order: 3, enabled: true, variant: "default" },
  { sectionType: "how_to_use", order: 4, enabled: true, variant: "default" },
  { sectionType: "science_research", order: 5, enabled: true, variant: "default" },
  { sectionType: "belief_shift_competitor", order: 6, enabled: true, variant: "default" },
  { sectionType: "farmstand_story", order: 7, enabled: true, variant: "default" },
  { sectionType: "timeline_results", order: 8, enabled: true, variant: "default" },
  { sectionType: "quiz_funnel", order: 9, enabled: false, variant: "default" },
  { sectionType: "reviews_grid", order: 10, enabled: true, variant: "default" },
  { sectionType: "faq_accordion", order: 11, enabled: true, variant: "default" },
  { sectionType: "final_cta", order: 12, enabled: true, variant: "default" },
];

const DEFAULT_SECTION_FORMULAS: Record<string, string> = {
  announcement_bar: "3-5 short ticker items separated by middle dot · (e.g. Always handmade · Always small-batch · Always shipped fresh)",
  hero_buy_box: "Outcome headline / Social proof line (rating + review count) / What's included list / Price today vs retail value / Savings amount and percent / Trust bullets (guarantee, shipping, freshness) / CTA button text",
  sticky_buy_bar: "Compact restatement: product name + price + CTA button text (appears when hero scrolls away)",
  product_recipe: "For each product in the bundle: product name / benefit headline / 3 tactical ingredient-benefit bullets / application note",
  how_to_use: "Step-by-step routine: Step 1 name + instruction / Step 2 name + instruction / Step 3 / Step 4 / Simple routine promise line",
  science_research: "Research headline / For each key ingredient: ingredient name + published benefit + study type / Disclaimer line",
  belief_shift_competitor: "Old belief statement / Why it's wrong / Who profits from that lie / The alternative / Brand belief statement / Optional competitor parody names",
  farmstand_story: "Origin headline / Location and people / How products are made / Authenticity proof / Invitation to visit or connect / Trust closing line",
  timeline_results: "Week 1 headline + changes / Week 2-3 headline + deeper changes / Month 2 headline + transformation / Usage tip / Consistency encouragement",
  quiz_funnel: "Quiz intro headline / Number of questions / Promise of personalized result / 5-6 quiz questions with 3-4 answer options each / Results copy template",
  reviews_grid: "Social proof headline / Rating line / 4-6 verified buyer reviews with name, rating, quote / Verified buyer badge text",
  faq_accordion: "6-8 objection-removing Q&A pairs covering: ingredients safety, results timeline, skin type, shipping/returns, subscription, guarantee, how-to-use, competitors",
  final_cta: "Closing urgency headline / Restate savings / Bundle contents recap / Trust bullets / CTA button text / Urgency/scarcity note",
};

export default function LandingPageTemplatesPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LandingPageTemplate | null>(null);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const { data: templates, isLoading } = useQuery<LandingPageTemplate[]>({
    queryKey: ["/api/landing-page-templates"],
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/landing-page-templates", {
        name: newName,
        description: newDescription || null,
        sections: DEFAULT_PAPA_BEAR_SECTIONS,
        sectionFormulas: DEFAULT_SECTION_FORMULAS,
      });
      return res.json();
    },
    onSuccess: (template) => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-page-templates"] });
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      navigate(`/landing-page-templates/${template.id}`);
    },
    onError: (e: any) => {
      toast({ title: "Failed to create template", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/landing-page-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-page-templates"] });
      toast({ title: "Template deleted" });
      setDeleteTarget(null);
    },
    onError: (e: any) => {
      toast({ title: "Failed to delete", description: e.message, variant: "destructive" });
    },
  });

  const filtered = templates?.filter(t => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }) ?? [];

  const sectionCount = (t: LandingPageTemplate) => {
    const sections = t.sections as any[];
    return sections?.length ?? 0;
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-templates-title">Landing Page Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage reusable section layouts and AI draft formulas</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="button-new-template">
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search templates..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
          data-testid="input-search-templates"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-20">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <LayoutTemplate className="h-7 w-7 text-primary" />
            </div>
            <h3 className="font-semibold tracking-tight mb-1.5 text-lg">No templates yet</h3>
            <p className="text-sm text-muted-foreground mb-1 text-center max-w-sm">
              Create a template to define section layouts and AI draft formulas for landing pages.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="grid-templates">
          {filtered.map(template => (
            <Card
              key={template.id}
              className="border-border/40 hover:border-primary/30 cursor-pointer transition-all group"
              onClick={() => navigate(`/landing-page-templates/${template.id}`)}
              data-testid={`card-template-${template.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3.5 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <LayoutTemplate className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold leading-snug line-clamp-1" data-testid={`text-template-name-${template.id}`}>
                          {template.name}
                        </p>
                        {template.isDefault && (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 shrink-0">
                            <Star className="h-2.5 w-2.5" /> Default
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{template.description}</p>
                      )}
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[11px] text-muted-foreground">
                          {sectionCount(template)} sections
                        </span>
                        {template.updatedAt && (
                          <span className="text-[11px] text-muted-foreground/60">
                            Updated {new Date(template.updatedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/30 opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteTarget(template);
                      }}
                      data-testid={`button-delete-template-${template.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="dialog-new-template">
          <DialogHeader>
            <DialogTitle>New Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="e.g. Papa Bear Long-Form"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                data-testid="input-template-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="What this template is for..."
                value={newDescription}
                onChange={e => setNewDescription(e.target.value)}
                rows={3}
                data-testid="input-template-description"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              New templates start with the default Papa Bear section layout. You can customize sections and formulas after creation.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newName.trim() || createMutation.isPending}
              data-testid="button-create-template"
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.name}". Existing landing pages using this template will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
